// Reports & Economy Dashboard
// Live-updating dummy data + filter-based datasets + free-text search

import React, { useState, useEffect } from "react";
import {
  CalendarClock,
  Download,
  Filter,
  LineChart as LineChartIcon,
  BarChart3,
  Percent,
  Users,
  PhoneCall,
  MessageSquare,
  Activity,
  FileText,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";

//--------------------------------------------
// 1) BASE DUMMY DATASETS (for filter changes)
//--------------------------------------------
const base7 = [
  { date: "01.12", minutes: 420, calls: 85, sms: 40 },
  { date: "02.12", minutes: 380, calls: 72, sms: 52 },
  { date: "03.12", minutes: 515, calls: 96, sms: 48 },
  { date: "04.12", minutes: 468, calls: 90, sms: 60 },
  { date: "05.12", minutes: 502, calls: 93, sms: 57 },
  { date: "06.12", minutes: 550, calls: 104, sms: 63 },
  { date: "07.12", minutes: 490, calls: 88, sms: 55 },
];

const base30 = Array.from({ length: 30 }).map((_, i) => {
  const m = 300 + Math.floor(Math.random() * 300);
  return {
    date: `${(i + 1).toString().padStart(2, "0")}.11`,
    minutes: m,
    calls: Math.floor(m / 5.2),
    sms: Math.floor(Math.random() * 70),
  };
});

const todayOnly = [{ date: "I dag", minutes: 540, calls: 102, sms: 48 }];
const yesterday = [{ date: "I går", minutes: 480, calls: 91, sms: 52 }];

//--------------------------------------------
// CHANNEL MIX + AGENT LOAD
//--------------------------------------------
const channelMixBase = [
  { channel: "Telefoni", minutes: 2450, count: 628 },
  { channel: "Video", minutes: 980, count: 142 },
  { channel: "Chat", minutes: 740, count: 310 },
];

const agentLoadBase = [
  { name: "Team A", utilization: 82, avgHandle: 11.4 },
  { name: "Team B", utilization: 76, avgHandle: 9.8 },
  { name: "Team C", utilization: 64, avgHandle: 13.1 },
  { name: "Team D", utilization: 58, avgHandle: 8.9 },
];

//--------------------------------------------
// Helpers
//--------------------------------------------
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

//--------------------------------------------
// KPI Card
//--------------------------------------------
const KpiCard = ({
  label,
  value,
  subLabel,
  trendLabel,
  trendDirection = "flat",
  icon: Icon,
}) => {
  const trendColor =
    trendDirection === "up"
      ? "text-emerald-600 bg-emerald-50"
      : trendDirection === "down"
      ? "text-rose-600 bg-rose-50"
      : "text-slate-500 bg-slate-50";

  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm gap-3">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
            {label}
          </span>
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-semibold text-slate-900">{value}</span>
            {subLabel && (
              <span className="text-[11px] text-slate-500">{subLabel}</span>
            )}
          </div>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-50">
          <Icon className="h-4 w-4 text-slate-600" />
        </div>
      </div>
      {trendLabel && (
        <div
          className={classNames(
            "inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium",
            trendColor,
          )}
        >
          <span className="leading-none">{trendLabel}</span>
        </div>
      )}
    </div>
  );
};

//--------------------------------------------
// MAIN COMPONENT
//--------------------------------------------
const ReportsModule = () => {
  const [range, setRange] = useState("last7");
  const [channelFilter, setChannelFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("day");
  const [search, setSearch] = useState("");

  const [callsData, setCallsData] = useState(base7);
  const [channelMix, setChannelMix] = useState(channelMixBase);
  const [agentLoad, setAgentLoad] = useState(agentLoadBase);

  //--------------------------------------------
  // FILTER LOGIC – dataset changes on selection
  //--------------------------------------------
  useEffect(() => {
    if (range === "today") setCallsData(todayOnly);
    else if (range === "yesterday") setCallsData(yesterday);
    else if (range === "last7") setCallsData(base7);
    else if (range === "last30") setCallsData(base30);
    else setCallsData(base7);
  }, [range]);

  //--------------------------------------------
  // LIVE DUMMY UPDATES (every 3 seconds)
  //--------------------------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setCallsData((prev) =>
        prev.map((item) => ({
          ...item,
          minutes: Math.max(120, item.minutes + (Math.random() * 40 - 20)),
          calls: Math.max(20, item.calls + Math.floor(Math.random() * 6 - 3)),
          sms: Math.max(5, item.sms + Math.floor(Math.random() * 4 - 2)),
        })),
      );

      setChannelMix((prev) =>
        prev.map((c) => ({
          ...c,
          minutes: Math.max(500, c.minutes + Math.floor(Math.random() * 200 - 100)),
          count: Math.max(40, c.count + Math.floor(Math.random() * 20 - 10)),
        })),
      );

      setAgentLoad((prev) =>
        prev.map((a) => ({
          ...a,
          utilization: Math.min(
            95,
            Math.max(40, a.utilization + (Math.random() * 4 - 2)),
          ),
        })),
      );
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleDownload = (format) => {
    console.log("Download", format, { range, channelFilter, groupBy, search });
  };

  //--------------------------------------------
  // FILTERED TABLE DATA (simple search demo)
  //--------------------------------------------
  const tableRows = callsData.map((row) => ({
    ...row,
    channel: "Telefoni",
    revenue: row.minutes * 0.18,
  }));

  const filteredRows = tableRows.filter((row) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      row.date.toLowerCase().includes(q) ||
      row.channel.toLowerCase().includes(q)
    );
  });

  //--------------------------------------------
  // RENDER
  //--------------------------------------------
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* HEADER */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="text-[11px] uppercase tracking-wide text-sky-500 font-semibold">
              Teletorget · Rapporter & Økonomi
            </span>
            <h1 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              Performance Dashboard
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
                Live nøkkeltall & historikk
              </span>
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              <CalendarClock className="h-4 w-4" /> Eksporter tidsplan
            </button>
            <button
              onClick={() => handleDownload("pdf")}
              className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-sky-700"
            >
              <Download className="h-4 w-4" /> Last ned rapport (PDF)
            </button>
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 py-4 flex flex-col gap-4">
          {/* SEARCH BAR */}
          <div className="w-full flex items-center justify-between mb-2 gap-3">
            <div className="text-[11px] text-slate-500">
              Fritekstsøk på dato, kanal, agent/kunde (API senere)
            </div>
            <input
              type="text"
              placeholder="Søk etter agent, kunde eller aktivitet..."
              className="rounded-full border border-slate-300 bg-white px-4 py-1.5 text-xs text-slate-900 shadow-sm w-64 focus:ring-sky-400 focus:border-sky-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* FILTER PANEL */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">Filter & segmentering</span>
            </div>
            <div className="flex flex-wrap gap-2 ml-auto">
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-900 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
              >
                <option value="today">I dag</option>
                <option value="yesterday">I går</option>
                <option value="last7">Siste 7 dager</option>
                <option value="last30">Siste 30 dager</option>
                <option value="custom">Egendefinert...</option>
              </select>

              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-900 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
              >
                <option value="all">Alle kanaler</option>
                <option value="voice">Telefoni</option>
                <option value="video">Video</option>
                <option value="chat">Chat</option>
                <option value="sms">SMS</option>
              </select>

              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-900 focus:border-sky-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-400"
              >
                <option value="day">Grupper etter dag</option>
                <option value="week">Grupper etter uke</option>
                <option value="month">Grupper etter måned</option>
              </select>
            </div>
          </section>

          {/* KPI ROW */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              label="Totalt antall minutter"
              value="2 890"
              subLabel="for valgt periode"
              trendLabel="Opp 8,2 % mot forrige periode"
              trendDirection="up"
              icon={PhoneCall}
            />
            <KpiCard
              label="Gjennomsnittsamtale"
              value="10,8 min"
              subLabel="per fullført samtale"
              trendLabel="Stabil utvikling"
              trendDirection="flat"
              icon={Percent}
            />
            <KpiCard
              label="Sendte SMS"
              value="412"
              subLabel="inkl. automatiske varsler"
              trendLabel="Ned 3,1 % mot forrige periode"
              trendDirection="down"
              icon={MessageSquare}
            />
            <KpiCard
              label="Onboardede kunder"
              value="36"
              subLabel="nye aktive konti"
              trendLabel="Opp 14,5 % mot forrige periode"
              trendDirection="up"
              icon={Users}
            />
          </section>

          {/* CHARTS ROW 1 */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Minutes & calls over time */}
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50">
                    <LineChartIcon className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-slate-800">
                      Minutter & antall samtaler
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Volum og utvikling for valgt periode
                    </span>
                  </div>
                </div>
                <span className="rounded-full bg-slate-50 px-2.5 py-0.5 text-[11px] text-slate-500">
                  Gruppering: {groupBy === "day" ? "Dag" : groupBy === "week" ? "Uke" : "Måned"}
                </span>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={callsData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis
                      yAxisId="left"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      stroke="#64748b"
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11, fill: "#64748b" }}
                      stroke="#64748b"
                    />
                    <Tooltip
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 8,
                        borderColor: "#e2e8f0",
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="minutes"
                      name="Minutter"
                      stroke="#0f766e"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="calls"
                      name="Samtaler"
                      stroke="#1d4ed8"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Channel mix */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50">
                    <BarChart3 className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-slate-800">
                      Kanalfordeling
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Minutter og volum per kanal
                    </span>
                  </div>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={channelMix} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="channel" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: "#e2e8f0" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="minutes" name="Minutter" fill="#0f766e" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="count" name="Antall hendelser" fill="#1d4ed8" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* CHARTS ROW 2 */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Agent utilization */}
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50">
                    <Activity className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-slate-800">
                      Agentutnyttelse
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Kapasitetsbruk per team
                    </span>
                  </div>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={agentLoad} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: "#e2e8f0" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="utilization"
                      name="Utnyttelse %"
                      stroke="#0f766e"
                      fill="#ccfbf1"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Live KPIs */}
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50">
                    <Users className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-slate-800">
                      Nøkkeltall (agenter & kunder)
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Påloggede agenter, samtidige sesjoner og kundevolum
                    </span>
                  </div>
                </div>
                <span className="rounded-full bg-slate-50 px-2.5 py-0.5 text-[11px] text-slate-500">
                  Live snapshot
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-[11px] text-slate-500">Påloggede agenter</span>
                  <span className="mt-1 text-sm font-semibold text-slate-900">18</span>
                  <span className="mt-0.5 text-[11px] text-emerald-600">+3 siden forrige time</span>
                </div>
                <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-[11px] text-slate-500">Aktive samtaler nå</span>
                  <span className="mt-1 text-sm font-semibold text-slate-900">7</span>
                  <span className="mt-0.5 text-[11px] text-slate-500">Kapasitet 61 %</span>
                </div>
                <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-[11px] text-slate-500">Kunder i kø</span>
                  <span className="mt-1 text-sm font-semibold text-slate-900">4</span>
                  <span className="mt-0.5 text-[11px] text-emerald-600">Under terskelverdi</span>
                </div>
                <div className="flex flex-col rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-[11px] text-slate-500">Est. omsetning (periode)</span>
                  <span className="mt-1 text-sm font-semibold text-slate-900">€ 12 480</span>
                  <span className="mt-0.5 text-[11px] text-emerald-600">+11,3 % mot forrige periode</span>
                </div>
              </div>

              <div className="mt-2 flex justify-end gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => handleDownload("csv")}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Last ned nøkkeltall (CSV)
                </button>
              </div>
            </div>
          </section>

          {/* TABULAR SECTION */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-50">
                  <FileText className="h-4 w-4 text-slate-600" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-slate-800">
                    Samlet aktivitetslogg (aggregert)
                  </span>
                  <span className="text-[11px] text-slate-500">
                    Grunnlag for eksport til regnskap, controlling og ekstern rapportering
                  </span>
                </div>
              </div>
              <div className="flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => handleDownload("csv")}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Eksporter filtrert datasett (CSV)
                </button>
                <button
                  type="button"
                  onClick={() => handleDownload("pdf")}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  Eksporter rapportlayout (PDF)
                </button>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full border-collapse text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">Dato</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">Kanal</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">Minutter</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">Samtaler</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">SMS</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-500">
                      Est. omsetning
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row.date} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-700">{row.date}</td>
                      <td className="px-3 py-2 text-slate-700">{row.channel}</td>
                      <td className="px-3 py-2 text-slate-700">{Math.round(row.minutes)}</td>
                      <td className="px-3 py-2 text-slate-700">{row.calls}</td>
                      <td className="px-3 py-2 text-slate-700">{row.sms}</td>
                      <td className="px-3 py-2 text-slate-700">
                        € {row.revenue.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ReportsModule;
